import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Plus, Trash, Check, X, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Centralized Locations module for the admin panel.
 *
 * Single source of truth for Regions & Cities — the SAME data renders in
 * BrowsePage filters, CreateListingPage, and the user profile dropdowns.
 *
 * Adds **manual reordering** on top of the existing CRUD endpoints:
 *  GET    /admin/structure/regions
 *  PUT    /admin/structure/regions/{name}        (create or rename)
 *  DELETE /admin/structure/regions/{name}
 *  POST   /admin/structure/regions/{name}/cities
 *  DELETE /admin/structure/regions/{region}/cities/{city}
 *  PUT    /admin/structure/regions-order         (NEW — bulk reorder)
 */
export const LocationsManagement = () => {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRegion, setExpandedRegion] = useState(null);
  const [showAddRegion, setShowAddRegion] = useState(false);
  const [newRegionName, setNewRegionName] = useState('');
  const [addingCityTo, setAddingCityTo] = useState(null);
  const [newCityName, setNewCityName] = useState('');

  const fetchRegions = () => {
    setLoading(true);
    axios.get(`${API}/admin/structure/regions`, { withCredentials: true })
      .then((res) => {
        // Sort by `order` ascending so the UI reflects the persisted ordering.
        const sorted = [...res.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setRegions(sorted);
      })
      .catch(() => toast.error('Failed to load regions'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRegions(); }, []);

  const persistOrder = async (next) => {
    setRegions(next);
    try {
      await axios.put(
        `${API}/admin/structure/regions-order`,
        next.map((r) => r.name),
        { withCredentials: true },
      );
    } catch {
      toast.error('Reorder failed');
      fetchRegions();
    }
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= regions.length) return;
    const next = [...regions];
    [next[idx], next[target]] = [next[target], next[idx]];
    persistOrder(next);
  };

  const handleAddRegion = async () => {
    const name = newRegionName.trim();
    if (!name) return;
    try {
      await axios.put(`${API}/admin/structure/regions/${encodeURIComponent(name)}`, { name, cities: [] }, { withCredentials: true });
      toast.success('Region added');
      setShowAddRegion(false); setNewRegionName('');
      fetchRegions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteRegion = async (name) => {
    if (!window.confirm(`Delete region "${name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/structure/regions/${encodeURIComponent(name)}`, { withCredentials: true });
      toast.success('Region deleted');
      fetchRegions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddCity = async (regionName) => {
    const cityName = newCityName.trim();
    if (!cityName) return;
    try {
      await axios.post(`${API}/admin/structure/regions/${encodeURIComponent(regionName)}/cities`, { name: cityName }, { withCredentials: true });
      toast.success('City added');
      setAddingCityTo(null); setNewCityName('');
      fetchRegions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteCity = async (regionName, cityName) => {
    try {
      await axios.delete(`${API}/admin/structure/regions/${encodeURIComponent(regionName)}/cities/${encodeURIComponent(cityName)}`, { withCredentials: true });
      toast.success('City deleted');
      fetchRegions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="py-8 text-center text-text_muted">Loading...</div>;

  const totalCities = regions.reduce((a, r) => a + (r.cities?.length || 0), 0);

  return (
    <div className="space-y-4" data-testid="locations-management">
      <div>
        <h2 className="text-lg font-semibold mb-1">Locations</h2>
        <p className="text-sm text-text_muted">
          Manage Regions & Cities used across listings, search filters, post ad and user profiles. Reorder rows with the arrows.
        </p>
      </div>

      <div className="flex justify-between items-center">
        <span className="text-sm text-text_muted">{regions.length} regions · {totalCities} cities</span>
        <Button size="sm" onClick={() => setShowAddRegion(true)} className="bg-primary hover:bg-primary-hover text-white gap-1.5" data-testid="add-region-btn">
          <Plus size={16} weight="bold" /> Add Region
        </Button>
      </div>

      {showAddRegion && (
        <div className="bg-surface border border-border rounded-lg p-4 flex gap-3 items-end" data-testid="add-region-form">
          <div className="flex-1">
            <Label className="text-xs text-text_muted mb-1 block">Region Name</Label>
            <Input
              value={newRegionName}
              onChange={(e) => setNewRegionName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddRegion()}
              placeholder="e.g. Vakinankaratra"
              className="border border-border text-sm"
              autoFocus
              data-testid="add-region-input"
            />
          </div>
          <Button size="sm" variant="success" onClick={handleAddRegion} data-testid="add-region-confirm"><Check size={16} /></Button>
          <Button size="sm" variant="outline" onClick={() => setShowAddRegion(false)} data-testid="add-region-cancel"><X size={16} /></Button>
        </div>
      )}

      {regions.map((reg, idx) => (
        <div key={reg.name} className="bg-white border border-border rounded-lg overflow-hidden" data-testid={`region-${reg.name}`}>
          <div className="flex items-center gap-2 p-4">
            {/* Reorder controls */}
            <div className="flex flex-col -my-1">
              <button
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                className="text-text_muted hover:text-primary disabled:opacity-30 disabled:hover:text-text_muted"
                data-testid={`region-${reg.name}-up`}
                aria-label="Move up"
              >
                <ArrowUp size={14} />
              </button>
              <button
                onClick={() => move(idx, 1)}
                disabled={idx === regions.length - 1}
                className="text-text_muted hover:text-primary disabled:opacity-30 disabled:hover:text-text_muted"
                data-testid={`region-${reg.name}-down`}
                aria-label="Move down"
              >
                <ArrowDown size={14} />
              </button>
            </div>
            <MapPin size={18} className="text-primary" />
            <button
              className="font-medium text-sm flex-1 text-left"
              onClick={() => setExpandedRegion(expandedRegion === reg.name ? null : reg.name)}
              data-testid={`region-${reg.name}-toggle`}
            >
              {reg.name}
            </button>
            <span className="text-xs bg-surface text-text_muted px-2 py-0.5 rounded">{reg.cities?.length || 0} cities</span>
            <button onClick={() => handleDeleteRegion(reg.name)} className="text-text_muted hover:text-danger" data-testid={`region-${reg.name}-delete`}><Trash size={16} /></button>
          </div>

          {expandedRegion === reg.name && (
            <div className="border-t border-border p-4 pl-12 bg-surface/30 space-y-1">
              {(reg.cities || []).map((city) => (
                <div key={city} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-white group" data-testid={`city-${city}`}>
                  <span className="text-text_main">{city}</span>
                  <button
                    onClick={() => handleDeleteCity(reg.name, city)}
                    className="ml-auto text-text_muted hover:text-danger opacity-0 group-hover:opacity-100"
                    data-testid={`city-${city}-delete`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
              {addingCityTo === reg.name ? (
                <div className="flex gap-2 items-center mt-2">
                  <Input
                    value={newCityName}
                    onChange={(e) => setNewCityName(e.target.value)}
                    placeholder="City name"
                    className="border border-border text-xs h-8 w-40"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCity(reg.name)}
                    data-testid="add-city-input"
                  />
                  <Button size="sm" variant="success" onClick={() => handleAddCity(reg.name)} className="h-8 px-2" data-testid="add-city-confirm"><Check size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => setAddingCityTo(null)} className="h-8 px-2" data-testid="add-city-cancel"><X size={14} /></Button>
                </div>
              ) : (
                <button
                  onClick={() => { setAddingCityTo(reg.name); setNewCityName(''); }}
                  className="text-xs text-primary hover:underline flex items-center gap-1 mt-2"
                  data-testid={`region-${reg.name}-add-city`}
                >
                  <Plus size={12} /> Add city
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
